/**
 * NavigationProgress Component
 * Shows loading indicator during React Router route transitions
 * Provides visual feedback for page navigation
 */

import { useNavigation } from 'react-router';
import { Spinner } from '@shopify/polaris';

/**
 * Navigation Progress indicator for route transitions
 *
 * This component monitors React Router navigation state and displays a fixed Polaris Spinner
 * in the top-right corner during page transitions. It provides clear visual feedback
 * that navigation is in progress without disrupting the current page content.
 *
 * The spinner automatically appears when navigation starts and disappears when complete.
 *
 * @example
 * ```tsx
 * // In root.tsx or layout component
 * import { NavigationProgress } from '~/components/NavigationProgress';
 *
 * export default function Root() {
 *   return (
 *     <Frame>
 *       <NavigationProgress />
 *       <Outlet />
 *     </Frame>
 *   );
 * }
 * ```
 */
export function NavigationProgress() {
  const navigation = useNavigation();

  // Show spinner during navigation
  const isNavigating = navigation.state === 'loading';

  if (!isNavigating) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 1000,
        backgroundColor: 'var(--p-color-bg-surface)',
        borderRadius: 'var(--p-border-radius-200)',
        padding: '8px',
        boxShadow: 'var(--p-shadow-200)',
      }}
      role="status"
      aria-label="Page loading"
    >
      <Spinner size="small" />
    </div>
  );
}
